// image-upload.js - Image Upload and Preview Management Module

(function(window, $) {
  'use strict';

  // Module initialization flag
  let initialized = false;

  // Anything the browser will render. Used only to ignore a non-image dropped on
  // the canvas; what may actually be PRINTED is decided by sniffPrintFormat below.
  const VALID_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp", "image/svg+xml"];

  // Hood modes: "image" or "text" — independent per side
  let hoodLeftMode = "image";
  let hoodRightMode = "image";

  // ===== PRINT ARTWORK MUST BE A REAL PNG =====
  //
  // Every design slot is uploaded to a key that ENDS IN .png, and everything
  // downstream believes that name. The upload path never re-encodes: a file is
  // re-encoded only when ImageAutoTrim actually crops transparent margins
  // (image-auto-trim-worker.js: convertToBlob "image/png"), and a JPEG has no
  // alpha to crop, so its own bytes go through untouched and land under a .png
  // key. Nothing after that point can read them.
  //
  // kgg (5 Sep 2026) is what this cost: a JPEG back logo saved as
  // back_large.png. The pHYs read found no chunk, so buildFilmPrints put the
  // print in `skipped`, the gang sheet came out with 24 fronts and no backs at
  // all, and the review page fell back to the config box and reported the
  // 40 cm HEIGHT CEILING as the print height (29 x 40 cm on a print that is
  // 29 x 20.8). Nothing failed anywhere: no error, no warning, no red flag.
  // A sweep of the bucket the same day found 928 more JPEGs under .png keys
  // across 82 order folders, so this is a habit and not one customer's mistake.
  //
  // SNIFF THE BYTES, NEVER file.type OR THE NAME. On macOS the browser fills
  // File.type from the EXTENSION, so the file that broke kgg presented itself
  // as image/png. A MIME check would have waved it through, which is why the
  // old check (still below, for "is this an image at all") could not catch it.
  const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

  // UNREADABLE IS NOT THE SAME AS WRONG. Every failure here resolves "png",
  // which lets the upload through: no browser quirk of ours may stand between
  // somebody and their artwork. That is safe to do now and was not before,
  // because the film refuses outright to build a sheet holding a print it
  // cannot size (handlers/gang-film-plan.js, PRINTS_UNSIZABLE), so a file this
  // check waves past can no longer go missing in silence. This gate exists to
  // catch it EARLY, not to be the only thing catching it.
  function sniffPrintFormat(file) {
    return new Promise(function(resolve) {
      try {
        if (typeof FileReader !== "function" || !file || typeof file.slice !== "function") {
          return resolve("png");
        }
        var fr = new FileReader();
        fr.onerror = function() { resolve("png"); };
        fr.onload = function(ev) {
          try {
            var b = new Uint8Array(ev.target.result);
            if (b.length >= 8 && PNG_MAGIC.every(function(v, i) { return b[i] === v; })) return resolve("png");
            if (b[0] === 0xff && b[1] === 0xd8) return resolve("JPEG");
            if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) return resolve("WebP");
            if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return resolve("GIF");
            if (b[0] === 0x3c) return resolve("SVG");
            if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return resolve("PDF");
            resolve("unknown");
          } catch (e) { resolve("png"); }
        };
        fr.readAsArrayBuffer(file.slice(0, 16));
      } catch (e) { resolve("png"); }
    });
  }

  function formatRejectionText(fmt) {
    if (fmt === "JPEG") {
      return "This is a JPEG, not a PNG. A JPEG has no transparent background, so it would print as a solid "
        + "rectangle, and our print files cannot read it at all. Export the artwork as a PNG and upload it again.";
    }
    if (fmt === "unknown") {
      return "This file is not a PNG and we cannot tell what it is. Print artwork has to be a PNG with a "
        + "transparent background. Export it as a PNG and upload it again.";
    }
    return "This is a " + fmt + " file, not a PNG. Print artwork has to be a PNG with a transparent background, "
      + "because that is the only format the print files can read. Export it as a PNG and upload it again.";
  }

  // Red, unlike the DPI note beside it, and that difference is deliberate: the
  // DPI note is a fact to notice and this one is a refusal. Left on the dropzone
  // after the alert is dismissed so the flag survives the click.
  function showFormatNotice(prefix, fmt) {
    if (!prefix) return;
    var $zone = $("#" + prefix + "_dropzone");
    if (!$zone.length) return;
    $zone.find(".ftm-dpi-note").remove();
    $zone.append($("<div/>", {
      "class": "ftm-dpi-note ftm-dpi-note--reject",
      title: formatRejectionText(fmt),
      text: fmt + " not accepted — upload a PNG",
    }));
  }

  // Files already cleared, so re-entering an upload handler does not re-read the
  // bytes. A WeakSet rather than a flag on the File: it cannot be defeated by a
  // frozen object, and it never keeps a 45 MB File alive.
  var formatCleared = typeof WeakSet === "function" ? new WeakSet() : null;
  function isFormatCleared(file) { return formatCleared ? formatCleared.has(file) : !!file._ftmFormatOk; }

  /**
   * Gate an upload on its real format. Calls `proceed` for a PNG and refuses
   * anything else. Sniffing is async, so every caller returns here and comes
   * back through its own front door once the file is cleared.
   */
  function gatePrintFormat(file, prefix, proceed) {
    var pass = function() {
      if (formatCleared) { try { formatCleared.add(file); } catch (e) { /* not an object */ } }
      else { try { file._ftmFormatOk = true; } catch (e) { /* frozen */ } }
      proceed();
    };
    sniffPrintFormat(file).then(function(fmt) {
      if (fmt === "png") return pass();
      showFormatNotice(prefix, fmt);
      alert(formatRejectionText(fmt));
    // A rejected promise here would strand the upload with nothing on screen,
    // which is a worse bug than the one this gate was written for.
    }).catch(pass);
  }

  // ===== DATA-URL LOADER =====
  // Routes through ImageAutoTrim (off-main-thread auto-crop of transparent margins).
  // Falls back to a plain FileReader read if the module isn't loaded or rejects —
  // the upload pipeline must never break.
  function loadDataURL(file) {
    if (window.ImageAutoTrim && typeof window.ImageAutoTrim.trim === "function") {
      return window.ImageAutoTrim.trim(file)
        .then(function(result) { return result.dataURL; })
        .catch(function() { return readFileAsDataURL(file); });
    }
    return readFileAsDataURL(file);
  }

  function readFileAsDataURL(file) {
    return new Promise(function(resolve, reject) {
      var fr = new FileReader();
      fr.onload = function(ev) { resolve(ev.target.result); };
      fr.onerror = function() { reject(fr.error || new Error("FileReader failed")); };
      fr.readAsDataURL(file);
    });
  }

  // Release the design-state lock and flush any uploads that queued while it was
  // held. Mirrors the color-picker's finally block so uploads and color swaps
  // serialize through the same gate.
  function releaseDesignLock() {
    window._designStateLocked = false;
    if (window._designStateLockQueue && window._designStateLockQueue.length > 0) {
      var queue = window._designStateLockQueue.splice(0);
      queue.forEach(function(fn) { try { fn(); } catch (e) { console.error("Queued upload error:", e); } });
    }
  }

  // ===== GENERIC PREVIEW FUNCTIONS =====
  // Convention: UI elements are named #{prefix}_preview, #{prefix}_placeholder, #{prefix}_remove, #{prefix}_dropzone

  function setBoxPreview(prefix, dataUrl) {
    $("#" + prefix + "_preview").attr("src", dataUrl).show();
    $("#" + prefix + "_placeholder").hide();
    $("#" + prefix + "_remove").show();
    $("#" + prefix + "_dropzone").removeClass("is-dragover").addClass("has-image");
  }

  function resetBoxPreview(prefix) {
    $("#" + prefix + "_preview").attr("src", "").hide();
    $("#" + prefix + "_placeholder").show();
    $("#" + prefix + "_remove").hide();
    $("#" + prefix + "_dropzone").removeClass("has-image");
    clearDpiNotice(prefix);
  }

  // ===== RESOLUTION NOTICE =====
  //
  // How sharp will this file be ON THE GARMENT? The answer is fixed the moment
  // it is uploaded — px over the cm the print ships at — and until now nothing
  // said it until the review page, which is after the mockups are made and,
  // for a cloud film, after three gigabytes of TIFF have been rendered.
  //
  // Jessheim's front logo went to film at 194 DPI. Nothing in the editor
  // mentioned it. That is the gap this closes, and it closes it at the only
  // moment the fix is cheap: while the person still has the artwork open and
  // can go and fetch a bigger export.
  //
  // ── The tiers are not ours ────────────────────────────────────────────────
  // 150 / 250 are review-report.mjs's DPI object, mirrored by the review page's
  // PSQ (dpiBad / dpiWarn) and checked by scripts/check-mirrors.mjs. A third
  // opinion about what "too soft" means is worse than no opinion: the editor
  // would wave a file through that the review then blocks, or nag about one it
  // accepts. Change them in review-report.mjs or not at all.
  // Kept as one literal so scripts/check-mirrors.mjs can read it the same way
  // it reads review-report.mjs's DPI and the review page's PSQ.
  var DPI_TIERS = { bad: 150, warn: 250 };

  // The cm this box actually prints at, from the renderer's own spec — the same
  // numbers the lambda sizes the file to. print-target-size.js owns the lookup
  // and the aspect-driven profiles (a zip front chest is 10-13 cm by shape, a
  // hood badge 8-11.5); this stays as a wrapper because the notice's arithmetic
  // is pinned through it in tests. Only the boxes the spec NAMES get a notice —
  // inventing a width would put a confident DPI figure under artwork nobody
  // measured, and silence is the honest answer for a box we cannot size.
  function targetWidthCm(productId, boxName, img) {
    if (!window.PrintTargetSize) return 0;
    return window.PrintTargetSize.widthCmFor(
      productId, boxName,
      img && (img.naturalWidth || img.width),
      img && (img.naturalHeight || img.height)
    );
  }

  function clearDpiNotice(prefix) {
    $("#" + prefix + "_dropzone").find(".ftm-dpi-note").remove();
  }

  // Tell the rest of the editor what this box now holds.
  //
  // A Canva draft arrives with a DPI per slot computed at import time against
  // the widest place the artwork could land, and canva-print-quality.js badges
  // the accordion rows and the product cards with it. Drop a new file into a
  // slot and that number is about a file which is no longer there: on VJ the
  // badge read 134 DPI while the upload sitting under it measured 218. The
  // measurement is announced from here because here is where it is made.
  //
  // `measurement` is null when the box cannot be sized — that is a genuine
  // "unknown", and an unknown badge beats a stale number.
  function announceArtwork(productId, boxName, measurement) {
    if (!boxName || typeof CustomEvent !== "function") return;
    try {
      document.dispatchEvent(new CustomEvent("ftm:artwork-measured", {
        detail: { productId: openCardId(productId), boxName: boxName, measurement: measurement }
      }));
    } catch (e) { /* never break an upload */ }
  }

  function activeProductId() {
    try {
      return (window.ProductConfig && window.ProductConfig.getActiveProductId
        && window.ProductConfig.getActiveProductId()) || "";
    } catch (e) { return ""; }
  }

  // A duplicated product edits under its BASE id and announces which duplicate
  // is open in window.currentEditingVersionId (single-product-creator.js:
  // enterGarmentEditor). Print verdicts are kept per duplicate, so an upload has
  // to name the card that is open, not the id the editor happens to run under.
  function openCardId(productId) {
    var v = window.currentEditingVersionId || window._editingHoodieVersionId;
    if (v && String(v).split("__v")[0] === String(productId || "").split("__v")[0]) return v;
    return productId;
  }

  // The box was emptied — the × in the sidebar, or deleting the item off the
  // canvas. Nothing prints there now, so no verdict should be shown for it.
  function announceArtworkCleared(productId, boxName) {
    if (!boxName || typeof CustomEvent !== "function") return;
    try {
      document.dispatchEvent(new CustomEvent("ftm:artwork-cleared", {
        detail: { productId: openCardId(productId), boxName: boxName }
      }));
    } catch (e) { /* never break a removal */ }
  }

  // Subtle by design: one muted line under the thumbnail, no icon, no colour at
  // "ok" (where it says nothing at all), and it never blocks the upload. The
  // person is mid-design; this is a fact to notice, not a dialog to dismiss.
  function showDpiNotice(prefix, productId, boxName, img) {
    clearDpiNotice(prefix);
    // The upload is auto-trimmed before it is placed (loadDataURL →
    // ImageAutoTrim), so these px are the ink, which is what gets scaled to the
    // print width. Measuring the untrimmed file would flatter every design with
    // a transparent margin, and would read squarer than the visible logo — both
    // halves matter, because the width itself is aspect-driven on four of the
    // profiles.
    var px = img && (img.naturalWidth || img.width);
    var m = window.PrintTargetSize && px
      ? window.PrintTargetSize.measure(productId, boxName, px, img && (img.naturalHeight || img.height))
      : null;
    announceArtwork(productId, boxName, m);
    if (!m) return;
    var dpi = m.dpi;
    var widthCm = m.widthCm;
    if (dpi >= DPI_TIERS.warn) return;
    var bad = dpi < DPI_TIERS.bad;
    var $zone = $("#" + prefix + "_dropzone");
    if (!$zone.length) return;
    $zone.append(
      $("<div/>", {
        "class": "ftm-dpi-note" + (bad ? " ftm-dpi-note--bad" : ""),
        title: px + " px across a print " + widthCm + " cm wide. 300 DPI is safe, "
          + DPI_TIERS.warn + " is fine on most artwork, under " + DPI_TIERS.bad + " looks soft on the garment.",
        text: bad
          ? dpi + " DPI — too low, use a bigger file"
          : dpi + " DPI — a little soft",
      })
    );
  }

  // ===== A REPLACEMENT KEEPS ITS PLACE =====
  //
  // Artwork dropped into a box that already holds artwork REPLACES the print
  // that is there; it does not start a new one. Where that print sits, how big
  // it comes out and which way it is turned belong to the BOX — somebody chose
  // them, or the import did — so the incoming file inherits them instead of
  // being re-fitted from nothing.
  //
  // That is also what makes a second colourway line up with the first, which is
  // how the fault was reported (2026-08-29):
  //
  //   "when i place a different color logo on the crewneck navy variant it gets
  //    placed lower than the placement than it was on the grey. i want it to
  //    always match the placement on the other color variant"
  //
  // A colour with no artwork of its own is shown a sibling's — loadFromVariant
  // and siblingExpansionLogo both fall back — so at the moment Navy's file
  // arrives, the item under it is Grå's, in Grå's place. Without this the new
  // item went through initImgToBox, which fits the whole editor box and centres
  // in it: the crewneck front is a 478 px box around a print about 173 px tall,
  // so Grå sat under the collar where its autoMap rule put it (centre y 235,
  // top 10 cm below the collar) and Navy landed at the box's centre y 360 —
  // 125 px lower, at belly height, and 8 % smaller for the padding. Two
  // colourways are the same garment; they cannot print in two different
  // places.
  //
  // An item is only ever in a box because some colourway put artwork there, so
  // "there is something to inherit from" and "another variant has this slot"
  // are the same condition. A genuinely first upload finds nothing and keeps
  // initImgToBox's auto-fit, which is still the only placement anyone has asked
  // for.

  // The list removeBoxItems and initImgToBox work on: DESIGN_ITEMS for the base
  // hoodie, EXPANSION_DESIGN_ITEMS while an expansion card is open. Asked
  // through the state accessor rather than the window mirror so the two can
  // never be read apart.
  function activeItems() {
    if (window.DESIGN && window.DESIGN.state && window.DESIGN.state.getActiveItems) {
      return window.DESIGN.state.getActiveItems() || [];
    }
    return window.ACTIVE_ITEMS || [];
  }

  // The predicate here has to be removeBoxItems' predicate: this reads the item
  // that call is about to destroy, and a box whose contents the two disagree
  // about would inherit from something that is still on the canvas.
  //
  // Backwards, because drawImages paints in array order: if a box holds more
  // than one item the LAST is the one on top, which is the print the person is
  // looking at and the one they mean to replace. An item too malformed to read
  // is stepped over rather than ending the search — a broken item underneath
  // must not cost a good one its place.
  function capturePlacement(partIndex, boxName) {
    var items = activeItems();
    for (var i = items.length - 1; i >= 0; i--) {
      var it = items[i];
      if (it.part_index !== partIndex || it.boxName !== boxName) continue;
      if (!it.PO || !(it.width > 0) || !(it.scaleX > 0) || !(it.scaleY > 0)) continue;
      return {
        PO: { x: it.PO.x, y: it.PO.y },
        width: it.width,
        scaleX: it.scaleX,
        scaleY: it.scaleY,
        rotate: it.rotate || 0
      };
    }
    return null;
  }

  // Wear the outgoing print's frame, then fit this file's own pixels back into
  // it. The scale is deliberately not copied across: it is a multiplier on a
  // pixel width and means nothing away from the file it was measured against,
  // and two colourways are almost never exported at the same pixel size —
  // Ringerike's grey wordmark is 4028 px wide and its navy recolour 4223, so a
  // copied scale would draw the second 327 px against the first's 312 — out by
  // a constant ratio, which no amount of dragging can reconcile. One uniform
  // factor on both axes, so a file with a slightly different aspect comes out
  // correctly shaped rather than stretched to the previous one's proportions.
  // This is EditMode.adoptImageDims' rule, which the colour swap and the
  // restore path already apply for the same reason.
  function applyPlacement(el_id, placement) {
    if (!placement) return;
    var items = activeItems();
    for (var i = items.length - 1; i >= 0; i--) {
      var it = items[i];
      if (it.el_id !== el_id) continue;
      if (!(it.width > 0)) return;
      var factor = placement.width / it.width;
      it.scaleX = placement.scaleX * factor;
      it.scaleY = placement.scaleY * factor;
      it.PO = { x: placement.PO.x, y: placement.PO.y };
      it.rotate = placement.rotate;
      if (window.DESIGN && window.DESIGN.drawImages) window.DESIGN.drawImages();
      return;
    }
  }

  // ===== GENERIC UPLOAD HANDLER =====
  // Looks up box from ProductConfig, handles file read, preview, canvas placement.
  // afterPlaceFn(box) is called after the image is placed on canvas for view-switching.
  function handleBoxUpload(boxName, file, uiPrefix, afterPlaceFn, formatOk) {
    if (!file) return;
    // If design state is locked (color swap or export in progress), queue the upload
    if (window._designStateLocked) {
      window._designStateLockQueue = window._designStateLockQueue || [];
      window._designStateLockQueue.push(function() { handleBoxUpload(boxName, file, uiPrefix, afterPlaceFn, formatOk); });
      return;
    }
    uiPrefix = uiPrefix || boxName;

    if (file.type && VALID_IMAGE_TYPES.indexOf(file.type.toLowerCase()) === -1) {
      alert("Please upload an image file (png, jpg, webp, svg).");
      return;
    }

    // The format gate. Sniffing is asynchronous, so this returns and comes back
    // through its own front door once the bytes are known to be a PNG. The
    // `formatOk` argument, not just the WeakSet, is what makes the re-entry
    // terminate on a browser where neither the set nor a File expando works.
    if (!formatOk && !isFormatCleared(file)) {
      gatePrintFormat(file, uiPrefix, function() {
        handleBoxUpload(boxName, file, uiPrefix, afterPlaceFn, true);
      });
      return;
    }

    var activeId = window.ProductConfig.getActiveProductId();
    var box = window.ProductConfig.getBoxByName(activeId, boxName);
    if (!box) return;

    if (window.loadPart) {
      window.loadPart(box.partIndex);
    }

    function placeDataURL(dataURL) {
      return new Promise(function(resolve) {
        setBoxPreview(uiPrefix, dataURL);

        // Populate ORIGINAL_IMAGES for product creation validation
        var origKeyMap = {
          "front": "front_image",
          "back_top": "back_top",
          "hood_left": "back_hood_left",
          "hood_right": "back_hood_right",
          // Same string on both sides, unlike the four above — see
          // FIELD_TO_PLACEMENT in variant-logo-util.js.
          "left_sleeve": "left_sleeve",
          "right_sleeve": "right_sleeve"
        };
        var origKey = origKeyMap[boxName];
        if (origKey && window.ORIGINAL_IMAGES) {
          window.ORIGINAL_IMAGES[origKey] = dataURL;
          // Store in per-variant logo backing store
          if (window.VariantLogoUtil) {
            window.VariantLogoUtil.storeForCurrentColor(origKey, dataURL);
          }
        }
        // What the file was CALLED. Groups name their uploads after the design
        // ("blå firkant.png"), which is the plainest statement of which
        // variantskjema token this card is that anybody makes — and it exists
        // only here: what reaches S3 is the slot name. See
        // katalog-artwork-scan.js.
        if (window.KatalogArtworkScan && file && file.name) {
          window.KatalogArtworkScan.noteUpload(
            window._editingHoodieVersionId || "hoodie", file.name);
        }

        var img = new Image();
        img.onload = function() {
          // Read before removeBoxItems throws the outgoing item away — see
          // "A replacement keeps its place" above.
          var placement = capturePlacement(box.partIndex, boxName);
          if (window.DESIGN && window.DESIGN.removeBoxItems) {
            window.DESIGN.removeBoxItems(box.partIndex, boxName);
          }
          if (window.d_item_sort !== undefined) {
            window.d_item_sort += 1;
          }
          try { showDpiNotice(uiPrefix, activeId, boxName, img); } catch (e) { /* never break an upload */ }
          var el_id = boxName + "_logo_" + (window.d_item_sort || 1);
          if (window.AddImage) {
            window.AddImage(img, el_id);
          }
          if (window.DESIGN && window.DESIGN.initImgToBox) {
            window.DESIGN.initImgToBox(img, el_id, box, box.partIndex, boxName);
          }
          applyPlacement(el_id, placement);
          if (afterPlaceFn) {
            afterPlaceFn(box);
          }
          resolve();
        };
        img.onerror = function() { resolve(); };
        img.src = dataURL;
      });
    }

    // Hold the design-state lock across BOTH the async trim and the canvas draw:
    // while locked, color-btn clicks are dropped (see color-picker), so the color
    // active at file-pick time is still active when we store + draw. Without this
    // a color switch mid-trim stored the artwork under the wrong color. Always
    // release (success or failure) so the lock can never stick.
    window._designStateLocked = true;
    loadDataURL(file)
      .then(placeDataURL)
      .then(releaseDesignLock, function(err) { console.error("Upload failed:", err); releaseDesignLock(); });
    // Note: file input clearing is handled by the thin wrapper functions
  }

  // ===== HOODIE THIN WRAPPERS =====
  function handleFrontFile(file) {
    handleBoxUpload("front", file, "front", function(box) {
      if (window.DesignAreas && window.DesignAreas.switchCanvasMode) {
        window.DesignAreas.switchCanvasMode(0, box, "#btn_mode_front_logo", "front");
      }
    });
    $("#input_front_logo").val("");
  }

  function handleBackTopFile(file) {
    handleBoxUpload("back_top", file, "back_top", function(box) {
      if (window.DesignAreas && window.DesignAreas.switchCanvasMode) {
        window.DesignAreas.switchCanvasMode(1, box, "#btn_mode_back_logo", "back");
      }
    });
    $("#input_back_top_logo").val("");
  }

  // Hood side file upload: side = "left" or "right"
  function handleHoodSideFile(side, file) {
    var boxName = "hood_" + side;
    handleBoxUpload(boxName, file, "hood_" + side, function(box) {
      if (window.DesignAreas && window.DesignAreas.switchCanvasMode) {
        window.DesignAreas.switchCanvasMode(1, box, "#btn_mode_hood_" + side + "_logo", "hood_" + side);
      }
    });
    $("#input_hood_" + side + "_logo").val("");
  }

  function handleHoodLeftFile(file)  { handleHoodSideFile("left", file); }
  function handleHoodRightFile(file) { handleHoodSideFile("right", file); }

  // The hoodie's own sleeve badges. Generic path, like the front: the box tells
  // handleBoxUpload which part to load and where to place the artwork, and the
  // origKeyMap above puts the upload into ORIGINAL_IMAGES + VARIANT_LOGOS under
  // the box's own name. Every OTHER product's sleeve badge is an expansion
  // upload and goes through handleExpansionBoxUpload.
  function handleSleeveFile(side, file) {
    var boxName = side + "_sleeve";
    handleBoxUpload(boxName, file, boxName, function(box) {
      if (window.DesignAreas && window.DesignAreas.switchCanvasMode) {
        window.DesignAreas.switchCanvasMode(box.partIndex, box, "#btn_mode_" + boxName, boxName);
      }
    });
    $("#input_" + boxName + "_logo").val("");
  }

  // ===== EXPANSION GENERIC UPLOAD HANDLER =====
  function handleExpansionBoxUpload(productId, boxName, file, logoStoreKey, formatOk) {
    if (!file) return;
    // If design state is locked (color swap or export in progress), queue the upload
    if (window._designStateLocked) {
      window._designStateLockQueue = window._designStateLockQueue || [];
      window._designStateLockQueue.push(function() { handleExpansionBoxUpload(productId, boxName, file, logoStoreKey, formatOk); });
      return;
    }
    var uiPrefix = "exp_" + boxName;

    if (file.type && VALID_IMAGE_TYPES.indexOf(file.type.toLowerCase()) === -1) {
      alert("Please upload an image file (png, jpg, webp, svg).");
      return;
    }

    // Same gate as handleBoxUpload; see the note there.
    if (!formatOk && !isFormatCleared(file)) {
      gatePrintFormat(file, uiPrefix, function() {
        handleExpansionBoxUpload(productId, boxName, file, logoStoreKey, true);
      });
      return;
    }

    var config = window.ProductConfig.getProduct(productId);
    if (!config) return;
    var box = config.boxes[boxName];
    if (!box) return;

    if (window.loadPart) {
      window.loadPart(box.partIndex);
    }

    function placeDataURL(dataURL) {
      return new Promise(function(resolve) {
        setBoxPreview(uiPrefix, dataURL);

        // Store in per-variant expansion logo backing store (version-aware key)
        if (window.VariantLogoUtil) {
          window.VariantLogoUtil.storeExpansionForCurrentColor(logoStoreKey || productId, boxName, dataURL);
        }
        if (window.KatalogArtworkScan && file && file.name) {
          window.KatalogArtworkScan.noteUpload(logoStoreKey || productId, file.name);
        }

        var img = new Image();
        img.onload = function() {
          // Read before removeBoxItems throws the outgoing item away — see
          // "A replacement keeps its place" above.
          var placement = capturePlacement(box.partIndex, boxName);
          if (window.DESIGN && window.DESIGN.removeBoxItems) {
            window.DESIGN.removeBoxItems(box.partIndex, boxName);
          }
          if (window.d_item_sort !== undefined) {
            window.d_item_sort += 1;
          }
          try { showDpiNotice(uiPrefix, productId, boxName, img); } catch (e) { /* never break an upload */ }
          var el_id = "exp_" + boxName + "_logo_" + (window.d_item_sort || 1);
          if (window.AddImage) {
            window.AddImage(img, el_id);
          }
          if (window.DESIGN && window.DESIGN.initImgToBox) {
            window.DESIGN.initImgToBox(img, el_id, box, box.partIndex, boxName);
          }
          applyPlacement(el_id, placement);
          // Restore sourceMapping on the newly created item (initImgToBox doesn't set it)
          var expCfg = window.ProductConfig && window.ProductConfig.getProduct(productId);
          if (expCfg && expCfg.mappings) {
            var activeItems = (window.DESIGN && window.DESIGN.state) ? window.DESIGN.state.getActiveItems() : [];
            var newItem = activeItems[activeItems.length - 1];
            if (newItem && newItem.boxName === boxName) {
              for (var mi = 0; mi < expCfg.mappings.length; mi++) {
                if (expCfg.mappings[mi].toBox === boxName) {
                  newItem.sourceMapping = expCfg.mappings[mi];
                  break;
                }
              }
            }
          }
          resolve();
        };
        img.onerror = function() { resolve(); };
        img.src = dataURL;
      });
    }

    // Hold the lock across trim + draw so an expansion color switch mid-trim cannot
    // store this upload under the wrong color (paired with the expansion swap's lock
    // check in product-creator-color-picker.js). Always release.
    window._designStateLocked = true;
    loadDataURL(file)
      .then(placeDataURL)
      .then(releaseDesignLock, function(err) { console.error("Expansion upload failed:", err); releaseDesignLock(); });
    $("#input_" + uiPrefix).val("");
  }

  // ===== PREVIEW RESET SHORTCUTS (the remove buttons and canvas-interaction.js) =====
  function resetFrontPreview()          { resetBoxPreview("front"); }
  function resetBackTopPreview()        { resetBoxPreview("back_top"); }

  // ===== HOOD TEXT RENDERING (per side) =====

  // Pure canvas factory — used by renderHoodSideText for the live edit and by
  // VariantLogoUtil.populateHoodTextPerColor at save time to mint per-color
  // dataUrls so dark variants get a white-text PNG (and Light grey keeps the
  // dark one). Returns { canvas, scale } so callers can both place into the
  // active design and grab toDataURL() — identical geometry guaranteed
  // because there's only one code path producing it.
  function renderHoodTextCanvas(text, boxWidth, textColor) {
    var RESOLUTION_SCALE = window.RESOLUTION_SCALE || 2;
    var scale = 4 * RESOLUTION_SCALE;

    var mCvs = document.createElement("canvas");
    mCvs.width = 1; mCvs.height = 1;
    var mCtx = mCvs.getContext("2d");

    // Auto-size font to fit box width with padding (mirrors the original).
    var maxWidth = boxWidth - 10;
    var fontSize = 60;
    mCtx.font = "400 " + fontSize + "px 'Bebas Neue', sans-serif";
    while (mCtx.measureText(text).width > maxWidth && fontSize > 10) {
      fontSize--;
      mCtx.font = "400 " + fontSize + "px 'Bebas Neue', sans-serif";
    }

    var metrics = mCtx.measureText(text);
    var textW = metrics.width;
    var textH = fontSize;
    if (metrics.actualBoundingBoxAscent !== undefined) {
      textH = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
    }

    var padPx = 8;
    var cvsW = Math.ceil(textW) + padPx * 2;
    var cvsH = Math.ceil(textH) + padPx * 2;

    var cvs_txt = document.createElement("canvas");
    cvs_txt.width = cvsW * scale;
    cvs_txt.height = cvsH * scale;
    var ctx = cvs_txt.getContext("2d");
    ctx.scale(scale, scale);

    ctx.font = "400 " + fontSize + "px 'Bebas Neue', sans-serif";
    ctx.fillStyle = textColor || "#000000";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, cvsW / 2, cvsH / 2);

    return { canvas: cvs_txt, scale: scale };
  }

  // Render a hood-text PNG for an arbitrary color (no DOM side effects) —
  // VariantLogoUtil.populateHoodTextPerColor calls this once per color
  // variant at save time so the correctly-tinted PNG ends up in
  // VARIANT_LOGOS[color].back_hood_<side> before fanout runs.
  function renderHoodTextForColor(side, text, textColor) {
    var t = String(text || "").toUpperCase().trim();
    if (!t) return null;
    var activeId = window.ProductConfig.getActiveProductId();
    var box = window.ProductConfig.getBoxByName(activeId, "hood_" + side);
    if (!box) return null;
    var fontSpec = "400 60px 'Bebas Neue', sans-serif";
    function build() {
      var out = renderHoodTextCanvas(t, box.width, textColor || "#000000");
      return out.canvas.toDataURL("image/png");
    }
    if (document.fonts && document.fonts.load) {
      return document.fonts.load(fontSpec).then(build).catch(build);
    }
    return Promise.resolve(build());
  }

  function renderHoodSideText(side) {
    var boxName = "hood_" + side;
    var origKey = "back_hood_" + side;
    var text = ($("#hood_" + side + "_text_input").val() || "").toUpperCase().trim();

    // Get hood box from ProductConfig
    var activeId = window.ProductConfig.getActiveProductId();
    var box = window.ProductConfig.getBoxByName(activeId, boxName);
    if (!box) return;

    // If empty, clear hood content for this side
    if (!text) {
      if (window.DESIGN && window.DESIGN.removeBoxItems) {
        window.DESIGN.removeBoxItems(box.partIndex, boxName);
      }
      if (window.ORIGINAL_IMAGES) {
        window.ORIGINAL_IMAGES[origKey] = null;
      }
      // Emptying the hood TEXT empties it in every colour: the text is one
      // field, and populateHoodTextPerColor renders it into every colourway
      // at save time, so the colours not on screen are holding the same text.
      // Clearing only the current colour (the × button's contract, right for
      // an upload) left "KG" in Grå after it was emptied while viewing Navy,
      // and the next autosave wrote it out as artwork (KG RS, 2026-09-20).
      clearHoodSideLogos(side, origKey);
      resetBoxPreview("hood_" + side);
      announceArtworkCleared(activeId, boxName);
      return;
    }

    var fontSpec = "400 60px 'Bebas Neue', sans-serif";

    function doRender() {
      var currentTextColor = (window.TeamWall && window.TeamWall.getTextColor)
        ? window.TeamWall.getTextColor()
        : "#000000";
      var rendered = renderHoodTextCanvas(text, box.width, currentTextColor);
      var cvs_txt = rendered.canvas;
      var scale = rendered.scale;

      // Place tight text image
      var textImg = new Image();
      textImg.onload = function() {
        // Save existing hood item position before removing
        var savedPO = null;
        var existingItems = window.ACTIVE_ITEMS || [];
        for (var si = 0; si < existingItems.length; si++) {
          if (existingItems[si].boxName === boxName && existingItems[si].part_index === box.partIndex) {
            if (existingItems[si].PO) {
              savedPO = { x: existingItems[si].PO.x, y: existingItems[si].PO.y };
            }
            break;
          }
        }

        if (window.DESIGN && window.DESIGN.removeBoxItems) {
          window.DESIGN.removeBoxItems(box.partIndex, boxName);
        }
        window.d_item_sort = (window.d_item_sort || 0) + 1;
        var el_id = boxName + "_text_" + window.d_item_sort;

        if (window.AddImage) {
          window.AddImage(textImg, el_id);
        }
        // Place in hood box (initImgToBox scales to fill)
        if (window.DESIGN && window.DESIGN.initImgToBox) {
          window.DESIGN.initImgToBox(textImg, el_id, box, box.partIndex, boxName, true);
        }

        // Override scale so bounding box is tight around text (1/scale = natural size)
        var items = window.ACTIVE_ITEMS || [];
        for (var i = items.length - 1; i >= 0; i--) {
          if (items[i].el_id === el_id) {
            items[i].scaleX = 1 / scale;
            items[i].scaleY = 1 / scale;
            // Restore saved position if user had dragged it
            if (savedPO) {
              items[i].PO = savedPO;
            }
            break;
          }
        }
        if (window.DESIGN && window.DESIGN.drawImages) {
          window.DESIGN.drawImages();
        }

        // Store for product creation validation
        if (window.ORIGINAL_IMAGES) {
          var hoodDataUrl = cvs_txt.toDataURL("image/png");
          window.ORIGINAL_IMAGES[origKey] = hoodDataUrl;
          if (window.VariantLogoUtil) {
            window.VariantLogoUtil.storeForCurrentColor(origKey, hoodDataUrl);
          }
        }

        // Show preview in sidebar
        setBoxPreview("hood_" + side, cvs_txt.toDataURL("image/png"));
        // Text, not artwork: rendered at HOOD_TEXT_RENDER_DPI and never soft,
        // so the slot carries no resolution verdict — and certainly not the one
        // the Canva import left on the image this replaced.
        announceArtworkCleared(activeId, boxName);
      };
      textImg.src = cvs_txt.toDataURL("image/png");
    }

    // Ensure font is loaded before rendering
    if (document.fonts && document.fonts.load) {
      document.fonts.load(fontSpec).then(doRender).catch(doRender);
    } else {
      doRender();
    }
  }

  // Backward compat wrapper
  function renderHoodText() { renderHoodSideText("right"); }

  function clearHoodSideContent(side) {
    var boxName = "hood_" + side;
    var origKey = "back_hood_" + side;
    var activeId = window.ProductConfig.getActiveProductId();
    var box = window.ProductConfig.getBoxByName(activeId, boxName);
    if (box && window.DESIGN && window.DESIGN.removeBoxItems) {
      window.DESIGN.removeBoxItems(box.partIndex, boxName);
    }
    if (window.ORIGINAL_IMAGES) {
      window.ORIGINAL_IMAGES[origKey] = null;
    }
    clearHoodSideLogos(side, origKey);
    resetBoxPreview("hood_" + side);
    announceArtworkCleared(activeId, boxName);
  }

  // Take a hood side's render out of VARIANT_LOGOS. An uploaded hood image is
  // per-colour, so only the colour on screen is cleared (the front/back remove
  // buttons' contract). A text hood is one field rendered into every colour,
  // so every colour is cleared, or the ones off screen keep a text that no
  // longer exists and the next save ships it.
  function clearHoodSideLogos(side, origKey) {
    if (!window.VariantLogoUtil) return;
    var mode = side === "left" ? hoodLeftMode : hoodRightMode;
    if (mode === "text" && window.VariantLogoUtil.clearForAllColors) {
      window.VariantLogoUtil.clearForAllColors(origKey);
    } else {
      window.VariantLogoUtil.clearForCurrentColor(origKey);
    }
  }

  function initHoodSideTextInput(side) {
    var timer;
    $("#hood_" + side + "_text_input").on("input", function() {
      clearTimeout(timer);
      var val = $(this).val();
      if (val && val.trim()) {
        $("#hood_" + side + "_text_remove").show();
      } else {
        $("#hood_" + side + "_text_remove").hide();
      }
      timer = setTimeout(function() { renderHoodSideText(side); }, 300);
    });

    $("#hood_" + side + "_text_remove").on("click", function() {
      $("#hood_" + side + "_text_input").val("");
      $(this).hide();
      clearHoodSideContent(side);
    });
  }

  function initHoodSideModeTabs(side) {
    $(".hood-" + side + "-mode-tab").on("click", function() {
      var mode = $(this).data("hood-mode");
      var currentMode = side === "left" ? hoodLeftMode : hoodRightMode;
      if (mode === currentMode) return;

      // Update tab UI
      $(".hood-" + side + "-mode-tab").removeClass("active");
      $(this).addClass("active");

      // Clear previous mode's content
      clearHoodSideContent(side);

      if (mode === "text") {
        if (side === "left") hoodLeftMode = "text"; else hoodRightMode = "text";
        $("#hood_" + side + "_image_container").hide();
        $("#hood_" + side + "_text_container").show();
        $("#hood_" + side + "_text_input").val("");
        $("#hood_" + side + "_text_remove").hide();
        $("#input_hood_" + side + "_logo").val("");
      } else {
        if (side === "left") hoodLeftMode = "image"; else hoodRightMode = "image";
        $("#hood_" + side + "_text_container").hide();
        $("#hood_" + side + "_image_container").show();
        $("#hood_" + side + "_text_input").val("");
        $("#hood_" + side + "_text_remove").hide();
      }
    });
  }

  // Dropzone initialization
  function initDropzone(zoneSelector, inputSelector, handleFileFn) {
    const $zone = $(zoneSelector);

    $zone.on("click", function () {
      $(inputSelector).click();
    });

    $zone.on("dragover dragenter", function (e) {
      e.preventDefault();
      e.stopPropagation();
      $zone.addClass("is-dragover");
    });

    $zone.on("dragleave dragend drop", function (e) {
      e.preventDefault();
      e.stopPropagation();
      $zone.removeClass("is-dragover");
    });

    $zone.on("drop", function (e) {
      const dt = e.originalEvent.dataTransfer;
      if (dt && dt.files && dt.files[0]) {
        handleFileFn(dt.files[0]);
      }
    });
  }

  function initFrontDropzone() {
    initDropzone("#front_dropzone", "#input_front_logo", handleFrontFile);
  }

  function initBackTopDropzone() {
    initDropzone("#back_top_dropzone", "#input_back_top_logo", handleBackTopFile);
  }

  function initHoodDropzones() {
    initDropzone("#hood_left_dropzone", "#input_hood_left_logo", handleHoodLeftFile);
    initDropzone("#hood_right_dropzone", "#input_hood_right_logo", handleHoodRightFile);
  }

  function initSleeveDropzones() {
    initDropzone("#left_sleeve_dropzone", "#input_left_sleeve_logo",
      function (f) { handleSleeveFile("left", f); });
    initDropzone("#right_sleeve_dropzone", "#input_right_sleeve_logo",
      function (f) { handleSleeveFile("right", f); });
  }

  // Initialize file input handlers
  function initFileInputs() {
    $("#input_front_logo").on("change", function () {
      if (this.files && this.files[0]) handleFrontFile(this.files[0]);
    });

    $("#input_back_top_logo").on("change", function () {
      if (this.files && this.files[0]) handleBackTopFile(this.files[0]);
    });

    $("#input_hood_left_logo").on("change", function () {
      if (this.files && this.files[0]) handleHoodLeftFile(this.files[0]);
    });

    $("#input_hood_right_logo").on("change", function () {
      if (this.files && this.files[0]) handleHoodRightFile(this.files[0]);
    });

    $("#input_left_sleeve_logo").on("change", function () {
      if (this.files && this.files[0]) handleSleeveFile("left", this.files[0]);
    });

    $("#input_right_sleeve_logo").on("change", function () {
      if (this.files && this.files[0]) handleSleeveFile("right", this.files[0]);
    });
  }

  // Initialize remove buttons
  function initRemoveButtons() {
    $("#front_remove").on("click", function (e) {
      e.stopPropagation();
      resetFrontPreview();
      announceArtworkCleared(activeProductId(), "front");
      if (window.ORIGINAL_IMAGES) { window.ORIGINAL_IMAGES.front_image = null; }
      if (window.VariantLogoUtil) { window.VariantLogoUtil.clearForCurrentColor("front_image"); }
      if (window.DESIGN && window.DESIGN.removeBoxItems) {
        window.DESIGN.removeBoxItems(0, "front");
      }
      if (window.loadPart) {
        window.loadPart(0);
      }
    });

    $("#back_top_remove").on("click", function (e) {
      e.stopPropagation();
      resetBackTopPreview();
      announceArtworkCleared(activeProductId(), "back_top");
      if (window.ORIGINAL_IMAGES) { window.ORIGINAL_IMAGES.back_top = null; }
      if (window.VariantLogoUtil) { window.VariantLogoUtil.clearForCurrentColor("back_top"); }
      if (window.DESIGN && window.DESIGN.removeBoxItems) {
        window.DESIGN.removeBoxItems(1, "back_top");
      }
      if (window.loadPart) {
        window.loadPart(1);
      }
    });

    // Hood left remove
    $("#hood_left_remove").on("click", function (e) {
      e.stopPropagation();
      resetBoxPreview("hood_left");
      announceArtworkCleared(activeProductId(), "hood_left");
      if (window.ORIGINAL_IMAGES) { window.ORIGINAL_IMAGES.back_hood_left = null; }
      clearHoodSideLogos("left", "back_hood_left");
      if (window.DESIGN && window.DESIGN.removeBoxItems) {
        window.DESIGN.removeBoxItems(1, "hood_left");
      }
      if (window.loadPart) { window.loadPart(1); }
      $("#hood_left_text_input").val("");
      $("#hood_left_text_remove").hide();
    });

    // Hood right remove
    $("#hood_right_remove").on("click", function (e) {
      e.stopPropagation();
      resetBoxPreview("hood_right");
      announceArtworkCleared(activeProductId(), "hood_right");
      if (window.ORIGINAL_IMAGES) { window.ORIGINAL_IMAGES.back_hood_right = null; }
      clearHoodSideLogos("right", "back_hood_right");
      if (window.DESIGN && window.DESIGN.removeBoxItems) {
        window.DESIGN.removeBoxItems(1, "hood_right");
      }
      if (window.loadPart) { window.loadPart(1); }
      $("#hood_right_text_input").val("");
      $("#hood_right_text_remove").hide();
    });

    // Sleeve badge removes. Same shape as the hood ones, minus the text box:
    // a sleeve print is an image and only an image.
    ["left", "right"].forEach(function (side) {
      var boxName = side + "_sleeve";
      $("#" + boxName + "_remove").on("click", function (e) {
        e.stopPropagation();
        resetBoxPreview(boxName);
        announceArtworkCleared(activeProductId(), boxName);
        if (window.ORIGINAL_IMAGES) { window.ORIGINAL_IMAGES[boxName] = null; }
        if (window.VariantLogoUtil) { window.VariantLogoUtil.clearForCurrentColor(boxName); }
        var box = window.ProductConfig.getBoxByName(activeProductId(), boxName);
        if (box && window.DESIGN && window.DESIGN.removeBoxItems) {
          window.DESIGN.removeBoxItems(box.partIndex, boxName);
        }
        if (box && window.loadPart) { window.loadPart(box.partIndex); }
      });
    });

    // Initially hide remove buttons
    $("#front_remove, #back_top_remove, #hood_left_remove, #hood_right_remove, #left_sleeve_remove, #right_sleeve_remove").hide();
  }

  // ===== EXPANSION PRODUCT UPLOAD INIT =====
  // Dynamically binds upload handlers for expansion product sidebar sections.
  // Called each time an expansion edit mode is entered.
  function initExpansionUploads(productId, logoStoreKey) {
    logoStoreKey = logoStoreKey || productId;
    var config = window.ProductConfig.getProduct(productId);
    if (!config) return;

    Object.keys(config.boxes).forEach(function(boxName) {
      // Skip team wall boxes (they use textarea, not file upload)
      var isTeamWall = false;
      for (var mi = 0; mi < config.mappings.length; mi++) {
        if (config.mappings[mi].toBox === boxName && config.mappings[mi].scaleMode === "reformat_teamwall") {
          isTeamWall = true; break;
        }
      }
      if (isTeamWall) return;

      var prefix = "exp_" + boxName;
      var destBox = config.boxes[boxName];
      if (!destBox) return;

      // Dropzone
      initDropzone("#" + prefix + "_dropzone", "#input_" + prefix, function(file) {
        handleExpansionBoxUpload(productId, boxName, file, logoStoreKey);
      });

      // File input
      $("#input_" + prefix).off("change").on("change", function() {
        if (this.files && this.files[0]) {
          handleExpansionBoxUpload(productId, boxName, this.files[0], logoStoreKey);
        }
      });

      // Remove button
      $("#" + prefix + "_remove").off("click").on("click", function(e) {
        e.stopPropagation();
        resetBoxPreview(prefix);
        announceArtworkCleared(logoStoreKey, boxName);
        if (window.DESIGN && window.DESIGN.removeBoxItems) {
          window.DESIGN.removeBoxItems(destBox.partIndex, boxName);
        }
        if (window.loadPart) { window.loadPart(destBox.partIndex); }
        // Clear from per-variant expansion logo backing store (version-aware key)
        if (window.VariantLogoUtil && logoStoreKey) {
          window.VariantLogoUtil.clearExpansionForCurrentColor(logoStoreKey, boxName);
        }
      });

      // Initially hide remove buttons
      $("#" + prefix + "_remove").hide();
    });
  }

  // ===== CANVAS AREA DROP ZONE =====
  // Allows dragging images from anywhere (e.g. Chrome download bar) onto the canvas
  function initCanvasDropzone() {
    var $container = $(".createGoods");
    if (!$container.length) return;

    var dragCounter = 0;

    // Prevent browser from navigating to dropped files
    $(document).on("dragover drop", function(e) {
      e.preventDefault();
    });

    $container.on("dragenter", function(e) {
      e.preventDefault();
      e.stopPropagation();
      var dt = e.originalEvent.dataTransfer;
      if (!dt || !dt.types || Array.prototype.indexOf.call(dt.types, "Files") === -1) return;
      dragCounter++;
      $container.addClass("canvas-dragover");
    });

    $container.on("dragover", function(e) {
      e.preventDefault();
      e.stopPropagation();
    });

    $container.on("dragleave", function(e) {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      if (dragCounter <= 0) {
        dragCounter = 0;
        $container.removeClass("canvas-dragover");
      }
    });

    $container.on("drop", function(e) {
      e.preventDefault();
      e.stopPropagation();
      dragCounter = 0;
      $container.removeClass("canvas-dragover");

      var dt = e.originalEvent.dataTransfer;
      if (!dt || !dt.files || !dt.files[0]) return;

      var file = dt.files[0];
      if (!file.type || VALID_IMAGE_TYPES.indexOf(file.type.toLowerCase()) === -1) return;

      var activeId = window.ProductConfig.getActiveProductId();
      var currentIndex = window.CURRENT_INDEX || 0;
      var boxesForView = window.ProductConfig.getBoxesForPartIndex(activeId, currentIndex);

      // Check if user has a specific box selected (highlighted) on this view
      var highlightBox = window.DesignAreas && window.DesignAreas.getHighlightBox ? window.DesignAreas.getHighlightBox() : null;
      var targetBox = null;

      // If there's a highlighted box, match it to a box name in the current view
      if (highlightBox) {
        for (var hName in boxesForView) {
          if (boxesForView.hasOwnProperty(hName) && boxesForView[hName] === highlightBox) {
            // Don't target team wall or text-only boxes
            if (hName !== "back_middle" && hName.indexOf("_teamwall") === -1) {
              var prod = window.ProductConfig.getProduct(activeId);
              if (!(prod && prod.placeholders && prod.placeholders[hName] && prod.placeholders[hName].type === "text")) {
                targetBox = hName;
              }
            }
            break;
          }
        }
      }

      // Fallback: pick first empty candidate box, or first candidate if all occupied
      if (!targetBox) {
        var candidateBoxes = [];
        for (var name in boxesForView) {
          if (!boxesForView.hasOwnProperty(name)) continue;
          if (name === "back_middle" || name.indexOf("_teamwall") !== -1) continue;
          var product = window.ProductConfig.getProduct(activeId);
          if (product && product.placeholders && product.placeholders[name] && product.placeholders[name].type === "text") continue;
          candidateBoxes.push(name);
        }

        if (candidateBoxes.length === 0) return;

        targetBox = candidateBoxes[0];
        for (var i = 0; i < candidateBoxes.length; i++) {
          if (window.DesignAreas && !window.DesignAreas.boxHasContent(currentIndex, candidateBoxes[i])) {
            targetBox = candidateBoxes[i];
            break;
          }
        }
      }

      if (window.ProductConfig.isBaseProduct()) {
        // Base product: use the thin wrappers that also handle view switching
        if (targetBox === "front") {
          handleFrontFile(file);
        } else if (targetBox === "back_top") {
          handleBackTopFile(file);
        } else if (targetBox === "hood_left") {
          handleHoodLeftFile(file);
        } else if (targetBox === "hood_right") {
          handleHoodRightFile(file);
        } else {
          handleBoxUpload(targetBox, file);
        }
      } else {
        var dragLogoKey = window.currentEditingVersionId || activeId;
        handleExpansionBoxUpload(activeId, targetBox, file, dragLogoKey);
      }
    });
  }

  // Main initialization function
  function initialize() {
    if (initialized) return;

    // Wait for jQuery
    if (typeof $ === 'undefined') {
      setTimeout(initialize, 50);
      return;
    }

    $(function() {
      initFrontDropzone();
      initBackTopDropzone();
      initHoodDropzones();
      initSleeveDropzones();
      initFileInputs();
      initRemoveButtons();
      initCanvasDropzone();
      initHoodSideTextInput("left");
      initHoodSideTextInput("right");
      initHoodSideModeTabs("left");
      initHoodSideModeTabs("right");
      initialized = true;
    });
  }

  // Public API
  window.ImageUpload = {
    initialize,
    // Generic
    handleBoxUpload,
    setBoxPreview,
    resetBoxPreview,
    // Hoodie
    handleFrontFile,
    handleBackTopFile,
    handleHoodLeftFile,
    handleHoodRightFile,
    resetFrontPreview,
    resetBackTopPreview,
    // Hood left
    getHoodLeftMode: function() { return hoodLeftMode; },
    setHoodLeftMode: function(mode) { hoodLeftMode = mode; },
    renderHoodLeftText: function() { renderHoodSideText("left"); },
    // Hood right
    getHoodRightMode: function() { return hoodRightMode; },
    setHoodRightMode: function(mode) { hoodRightMode = mode; },
    renderHoodRightText: function() { renderHoodSideText("right"); },
    // Generic hood side render
    renderHoodSideText: renderHoodSideText,
    // Per-color text-mode hood renderer (used by VariantLogoUtil.populateHoodTextPerColor
    // at save time to mint a correctly-tinted PNG for every color variant).
    renderHoodTextForColor: renderHoodTextForColor,
    // Backward compat
    renderHoodText: renderHoodText,
    // Expansion (generic)
    initExpansionUploads,
    handleExpansionBoxUpload,
    // Resolution notice. Exposed so its arithmetic and its silence can be
    // pinned in tests without driving a file input — the tiers are mirrored
    // from review-report.mjs and drift is the failure that matters.
    targetWidthCm,
    showDpiNotice,
    clearDpiNotice,
    DPI_TIERS: DPI_TIERS
  };

  // Auto-initialize if document is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

})(window, jQuery);
